import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface NewTicket {
  id: number;
  title: string;
  ticketNumber: string;
  status: number | null;
  priority: number | null;
  queueID: number | null;
  assignedResourceID: number | null;
  companyID: number | null;
}

interface NotificationContextValue {
  newTickets: NewTicket[];
  /** Removes one ticket from the bell. */
  dismissNotification: (id: number) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  newTickets: [],
  dismissNotification: () => {},
  clearNotifications: () => {},
});

/**
 * The bell's list is kept per tab in sessionStorage, so a reload doesn't empty it (and dismissed tickets stay
 * dismissed). Closing the tab starts afresh, as before — the server only pushes tickets created while connected.
 */
const STORE_KEY = 'cosmos:notifications';
const MAX = 20;

function loadTickets(): NewTicket[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(STORE_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as NewTicket[]).filter((t) => typeof t?.id === 'number').slice(0, MAX) : [];
  } catch { return []; }
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [newTickets, setNewTickets] = useState<NewTicket[]>(loadTickets);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(newTickets)); } catch { /* storage unavailable */ }
  }, [newTickets]);

  const connect = useCallback(() => {
    const es = new EventSource('/api/notifications/stream');
    esRef.current = es;

    es.addEventListener('new-ticket', (e: MessageEvent) => {
      try {
        const ticket = JSON.parse(String(e.data)) as NewTicket;
        setNewTickets((prev) => {
          if (prev.some((t) => t.id === ticket.id)) return prev;
          return [ticket, ...prev].slice(0, MAX);
        });
      } catch {
        // ignore malformed events
      }
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;
      setTimeout(connect, 5_000);
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      esRef.current?.close();
    };
  }, [connect]);

  const dismissNotification = useCallback((id: number) => setNewTickets((prev) => prev.filter((t) => t.id !== id)), []);
  const clearNotifications = useCallback(() => setNewTickets([]), []);

  return (
    <NotificationContext.Provider value={{ newTickets, dismissNotification, clearNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
